import { format } from "date-fns";
import { every, some } from "lodash";
import * as React from "react";
import ReactMarkdown from "react-markdown";
import AnswerableOption from "./AnswerableOption";

import { FcComments, FcFile, FcPlus } from "react-icons/fc";
import { HiExclamationCircle, HiTrash } from "react-icons/hi";
import remarkGfm from "remark-gfm";
import { Account, useUser } from "../utils/AccountStore";
import { Comment, IQuestionnaireInstance, Question, Questionnaire, getSubmissionData, validate } from "../utils/ServiceManagerAPI";
import { useDarkMode } from "./DarkModeSwitch";
import RenderAttachments from "./RenderAttachments";
import StyledModal from "./StyledModel";

export interface QuestionDataValidationAPIModel {
    id: string;
    questionID: string;
    name: string;
    range: string;
    regularExpression: string;
    length: string;
    evaluate: string;
    optionsAppliedTo: string[];
}

export const markdownComponents = {
    table: (props) => <table className="w-full border-collapse border border-gray-300 dark:border-gray-600" {...props} />,
    tr: (props) => <tr className="border-b border-gray-200 dark:border-gray-700" {...props} />,
    td: (props) => <td className="border border-gray-300 dark:border-gray-600 p-2 text-center" {...props} />,
    th: (props) => <th className="border border-gray-300 dark:border-gray-600 p-2 text-center font-semibold bg-gray-100 dark:bg-gray-800" {...props} />,
    tbody: (props) => <tbody {...props} />,
    thead: (props) => <thead {...props} />,
};

interface AnswerableQuestionProps {
    loading: boolean;
    currentQuestionnaire?: Questionnaire;
    question: Question;
    handleOptionChange: Function;
    deleteSet?: Function;
    addSet?: Function;
    toggleAttachmentModal?: Function;
    toggleCommentModal?: Function;
    deleteAttachment?: Function;
    deleteComment?: Function;
    similarQuestionnaires: Questionnaire[];
    questionnaires: IQuestionnaireInstance[];
}

export const AnswerableQuestion = ({
    loading,
    currentQuestionnaire,
    question,
    handleOptionChange,
    deleteSet,
    addSet,
    toggleAttachmentModal,
    toggleCommentModal,
    deleteAttachment,
    deleteComment,
    similarQuestionnaires,
    questionnaires,
}: AnswerableQuestionProps) => {
    const [isSmall, setIsSmall] = React.useState(false);
    
    React.useEffect(() => {
        const checkSize = () => {
            setIsSmall(window.innerWidth < 768);
        };
        checkSize();
        window.addEventListener("resize", checkSize);
        return () => window.removeEventListener("resize", checkSize);
    }, []);

    const [valid, setValid] = React.useState(question.dataValidations?.length == 0);
    const [validationMessage, setValidationMessage] = React.useState("");
    const { user, setUser } = useUser();

    const doValidation = (overrideOptionValue?, overrideOptionId?) => {
        let valid = true;
        if (currentQuestionnaire && question.dataValidations) {
            let messages = [] as string[];
            question.dataValidations.forEach((validation) => {
                question.options?.forEach((option) => {
                    if (!validation.optionsAppliedTo || validation.optionsAppliedTo.length == 0 || validation.optionsAppliedTo.includes(option.id)) {
                        let test = validate(validation, option.value, question, getSubmissionData(currentQuestionnaire));
                        if (overrideOptionId && option.id == overrideOptionId) {
                            test = overrideOptionValue;
                        }
                        valid = valid && test;
                        if (!test) {
                            messages.push(validation.name);
                        }
                    }
                });
            });
            setValidationMessage(messages.join(", "));
        }
        setValid(valid);
    };

    React.useEffect(() => {
        doValidation();
    }, [question]);

    const [isDarkMode] = useDarkMode();

    const useGridLayout = currentQuestionnaire?.questionnaireType?.useGridLayout || false;

    const getQuestionColor = () => {
        return `var(${isDarkMode ? "--question-color-dark" : "--question-color-light"},${isDarkMode ? "var(--heading-color-dark)" : "var(--heading-color-light)"})`;
    };

    return (
        <div>
            <div className={`grid ${useGridLayout ? "grid-cols-1 md:grid-cols-4" : "grid-cols-1"} gap-4`}>
                <div className={useGridLayout ? "md:col-span-1" : ""}>
                    {/* Question Text, AutoFill and Links */}
                    <div>
                        <div className="flex flex-row gap-2 items-center w-full">
                            <span className="text-lg font-medium" style={{ color: getQuestionColor() }}>
                                {question.text}
                                {question.required && (
                                    <span 
                                        className="p-0.5" 
                                        style={{ 
                                            color: question.answers?.length != 0 ? "var(--background-front)" : "#dc2626" 
                                        }}
                                    >
                                        *
                                    </span>
                                )}
                            </span>
                            {!valid && (
                                <div className="relative group">
                                    <HiExclamationCircle color="red" />
                                    <div className="absolute hidden group-hover:block z-50 bg-gray-900 text-white text-xs rounded px-2 py-1 -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                                        {validationMessage}
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center">
                                {/* Copy answer UI removed */}
                            </div>
                        </div>
                    </div>
                    {question.subtext && (
                        <div>
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                {question.subtext}
                            </ReactMarkdown>
                        </div>
                    )}
                </div>

                {(!questionnaires || questionnaires.length == 1) && (
                    <div className={useGridLayout ? "md:col-span-3" : ""}>
                        <div className={`grid ${isSmall || !currentQuestionnaire?.allowComments ? "grid-cols-1" : "grid-cols-5"} gap-4`}>
                            <div className={isSmall || !currentQuestionnaire?.allowComments ? "" : "col-span-3"}>
                                <RenderQuestionOptions
                                    questionnaire={currentQuestionnaire}
                                    question={question}
                                    handleOptionChange={handleOptionChange}
                                    loading={loading}
                                    deleteSet={deleteSet}
                                    addSet={addSet}
                                    disabled={false}
                                    validate={true}
                                    onValidate={doValidation}
                                ></RenderQuestionOptions>
                            </div>
                            <div className={isSmall || !currentQuestionnaire?.allowComments ? "" : "col-span-2"}>
                                <RenderQuestionCommentsAndUploads
                                    currentQuestionnaire={currentQuestionnaire}
                                    question={question}
                                    user={user}
                                    deleteComment={deleteComment}
                                    toggleCommentModal={toggleCommentModal}
                                    toggleAttachmentModal={toggleAttachmentModal}
                                    deleteAttachment={deleteAttachment}
                                    questionnaires={questionnaires}
                                ></RenderQuestionCommentsAndUploads>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

function RenderQuestionCommentsAndUploads({
    currentQuestionnaire,
    question,
    user,
    deleteComment,
    toggleCommentModal,
    toggleAttachmentModal,
    deleteAttachment,
    questionnaires,
}: {
    currentQuestionnaire?: Questionnaire;
    question: Question;
    user: Account;
    deleteComment?: Function;
    toggleCommentModal?: Function;
    toggleAttachmentModal?: Function;
    deleteAttachment?: Function;
    questionnaires: IQuestionnaireInstance[];
}) {
    return (
        <div>
            {question.allowComments && (
                <RenderQuestionComments
                    currentQuestionnaire={currentQuestionnaire}
                    question={question}
                    user={user}
                    deleteComment={deleteComment}
                    questionnaires={questionnaires}
                ></RenderQuestionComments>
            )}
            {question.allowUploads && (
                <RenderAttachments questionnaire={currentQuestionnaire} attachments={question?.attachments || []} user={user} deleteAttachment={deleteAttachment}></RenderAttachments>
            )}
            {!currentQuestionnaire?.locked && (
                <div className="flex items-center flex-row gap-2 justify-end">
                    {question.allowComments && currentQuestionnaire?.allowComments && user.hasQuestionnaireAccess(currentQuestionnaire?.id, 16) && (
                        <div 
                            className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                            onClick={() => toggleCommentModal && toggleCommentModal(question.id)}
                            title="Add Comment"
                        >
                            <FcComments size="24px" />
                        </div>
                    )}
                    {question.allowUploads && currentQuestionnaire?.allowUploads && user.hasQuestionnaireAccess(currentQuestionnaire?.id, 8) && (
                        <div 
                            className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                            onClick={() => toggleAttachmentModal && toggleAttachmentModal(question.id)}
                            title="Add Files"
                        >
                            <FcFile size="24px" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function RenderQuestionComments({
    questionnaires,
    currentQuestionnaire,
    question,
    user,
    deleteComment,
}: {
    questionnaires: IQuestionnaireInstance[];
    currentQuestionnaire?: Questionnaire;
    question: Question;
    user: Account;
    deleteComment?: Function;
}) {
    const [isSmall, setIsSmall] = React.useState(false);
    
    React.useEffect(() => {
        const checkSize = () => {
            setIsSmall(window.innerWidth < 768);
        };
        checkSize();
        window.addEventListener("resize", checkSize);
        return () => window.removeEventListener("resize", checkSize);
    }, []);

    const [showDeleteModal, setShowDeleteModal] = React.useState(false);

    let comments = questionnaires
        ?.flatMap((questionnaire) => questionnaire.questionnaire.questions)
        ?.flatMap((question) => question?.comments)
        ?.filter((c) => c && c?.questionID == question.id);

    const [commentToDelete, setCommentToDelete] = React.useState<Comment>();
    const [loading, setLoading] = React.useState(false);

    const doDelete = async () => {
        setLoading(true);
        if (commentToDelete?.id && deleteComment) {
            await deleteComment(question.id, commentToDelete.id);
        }
        setShowDeleteModal(false);
        setLoading(false);
    };

    return (
        <>
            {question.allowComments && (user.hasQuestionnaireAccess(currentQuestionnaire?.id, 16) || user.hasCampaignAccess(currentQuestionnaire?.campaignID, 16)) && (
                <div className="flex flex-col gap-2 pl-2">
                    {comments?.map((comment, commentKey) => (
                        <div key={`q${question.id}c${commentKey}`}>
                            <div className={`grid ${isSmall ? "grid-cols-1" : "grid-cols-[150px_auto_40px]"} gap-2`}>
                                <div>
                                    <span className="text-sm text-gray-800 dark:text-gray-200">{comment.username}</span>
                                    {comment.timestamp && <div className="text-xs text-gray-500 dark:text-gray-400">{format(comment.timestamp, "dd/MM/yyyy HH:mm")}</div>}
                                </div>
                                <span className="text-sm text-gray-800 dark:text-gray-200">{comment.text}</span>
                                <div className="flex items-center justify-center">
                                    {!currentQuestionnaire?.locked && deleteComment && comment.questionnaireID == currentQuestionnaire?.id && user.id == comment.userID && (
                                        <button
                                            className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900 rounded transition-colors"
                                            onClick={() => {
                                                setShowDeleteModal(true);
                                                setCommentToDelete(comment);
                                            }}
                                            title="Delete Comment"
                                        >
                                            <HiTrash size="20px" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    <StyledModal
                        isOpen={showDeleteModal}
                        setIsOpen={() => {
                            setShowDeleteModal(false);
                            setCommentToDelete(undefined);
                        }}
                    >
                        <div className="w-full max-w-md">
                            <span className="text-gray-800 dark:text-gray-200">Are you sure you want to delete this comment?</span>
                            <div className="flex flex-row justify-end p-2 gap-2">
                                <button 
                                    onClick={() => setShowDeleteModal(false)}
                                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                >
                                    Cancel
                                </button>
                                {commentToDelete && (
                                    <button 
                                        onClick={() => doDelete()}
                                        disabled={loading}
                                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                                    >
                                        Confirm
                                    </button>
                                )}
                            </div>
                        </div>
                    </StyledModal>
                </div>
            )}
        </>
    );
}

interface RenderQuestionOptionsProps {
    questionnaire?: Questionnaire;
    question: Question;
    handleOptionChange: Function;
    loading: boolean;
    deleteSet?: Function;
    addSet?: Function;
    disabled: boolean;
    validate: boolean;
    onValidate: Function;
}

function RenderQuestionOptions({ questionnaire, question, handleOptionChange, loading, deleteSet, addSet, disabled, validate, onValidate }: RenderQuestionOptionsProps) {
    return (
        <>
            {question.allowSets && (
                <div>
                    {question.sets?.map((set, setKey) => (
                        <div className="flex gap-1 flex-row" key={`q${question.id}s${setKey}`}>
                            <div>
                                <QuestionOptions
                                    question={question}
                                    options={set.options}
                                    handleOptionChange={handleOptionChange}
                                    setId={set.id}
                                    disabled={questionnaire?.locked || disabled}
                                    onValidate={onValidate}
                                    validate={validate}
                                    gridView={!!questionnaire?.questionnaireType?.useGridLayout}
                                ></QuestionOptions>
                            </div>
                            {deleteSet && (
                                <div>
                                    <div 
                                        className="rounded-full p-2 flex items-center justify-center hover:bg-red-100 dark:hover:bg-red-900 cursor-pointer text-red-600 transition-colors"
                                        onClick={() => deleteSet(question.id, set.id)}
                                        title="Delete Set"
                                    >
                                        x
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                    {addSet && (
                        <div className="flex flex-row">
                            <button
                                disabled={questionnaire?.locked || loading}
                                onClick={() => addSet(question.id)}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                            >
                                <FcPlus size="24px" />
                                Add an answer
                            </button>
                        </div>
                    )}
                </div>
            )}
            {!question.allowSets && (
                <>
                    <div>
                        <QuestionOptions
                            question={question}
                            options={question.options || []}
                            disabled={questionnaire?.locked || disabled}
                            handleOptionChange={handleOptionChange}
                            setId={null}
                            onValidate={onValidate}
                            validate={validate}
                            gridView={!!questionnaire?.questionnaireType?.useGridLayout}
                        ></QuestionOptions>
                    </div>
                </>
            )}
        </>
    );
}

interface QuestionOptionsProps {
    question: Question;
    options: any[];
    handleOptionChange: Function;
    setId: string | null;
    disabled: boolean;
    onValidate: Function;
    validate: boolean;
    gridView: boolean;
}

function QuestionOptions({ question, options, handleOptionChange, setId, disabled, onValidate, validate, gridView }: QuestionOptionsProps) {
    let onlySelections = every(options, (o) => o.type === 0);
    let atomic = some(options, (o) => o.type === 0) && (!every(options, (o) => o.type === 0) || options.length == 1);
    let gapSize = "gap-1";
    {
        !atomic ? (gapSize = "gap-1") : (gapSize = "gap-4");
    }

    let longTextOnly = every(options, (o) => o.type === 7);

    return (
        <div className={`${onlySelections ? "pl-2" : ""}`}>
            <div className={`${longTextOnly || options.length == 1 ? "flex-col" : "flex-row flex-wrap"} flex ${gapSize}`}>
                {options.map((option, optionKey) => (
                    <AnswerableOption
                        question={question}
                        key={`q${question.id}o${optionKey}`}
                        style={{}}
                        option={option}
                        value={option.value}
                        changed={(value) => handleOptionChange(value, option, question.id, setId)}
                        atomic={atomic}
                        disabled={disabled}
                        onValidate={onValidate}
                        validate={validate}
                    ></AnswerableOption>
                ))}
            </div>
            {!disabled && (question?.options || []).length > 0 && !gridView && (
                <>
                    {question.multipleOptions &&
                        every(question.options, {
                            type: 0,
                        }) && (
                            <span className="text-gray-500 dark:text-gray-400 text-sm">
                                Choose multiple
                            </span>
                        )}
                    {!question.multipleOptions && every(question.options, { type: 0 }) && (
                        <span className="text-gray-500 dark:text-gray-400 text-sm">
                            Choose one
                        </span>
                    )}
                </>
            )}
        </div>
    );
}

export default AnswerableQuestion;
